/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import { batchActions } from 'redux-batched-actions';
// material
import { displaySuccessMessage, displayErrorMessage } from '@insticator/insticator-ui';
// utils
import { asyncGetAddOns, asyncCreateAddOnsSetting, asyncUpdateAddOnsSetting } from 'utils/api/addons';
import { asyncUploadImage } from 'utils/api/commenting';
import { typeChangedFromOriginal } from 'state/reducers/embedsState/embedManagementState/contentStatus/content_status_helpers';

/* ++++++++++ --------------- EXPORTS --------------- ++++++++++ */
export const GET_ADDONS = 'GET_ADDONS';
export const GET_COMMENTING_ADDONS_FETCHING = 'GET_COMMENTING_ADDONS_FETCHING';
export const GET_COMMENTING_ADDONS_SUCCESS = 'GET_COMMENTING_ADDONS_SUCCESS';
export const GET_COMMENTING_ADDONS_ERROR = 'GET_COMMENTING_ADDONS_ERROR';
export const COMMENTING_ADDONS_SETTINGS_CHANGED = 'COMMENTING_ADDONS_SETTINGS_CHANGED';
export const CREATE_COMMENTING_ADDONS_SETTING_SUCCESS = 'CREATE_COMMENTING_ADDONS_SETTING_SUCCESS';
export const UPDATE_COMMENTING_ADDONS_SETTING_SUCCESS = 'UPDATE_COMMENTING_ADDONS_SETTING_SUCCESS';
export const UPDATE_COMMENTING_ADDONS_SETTING_ERROR = 'UPDATE_COMMENTING_ADDONS_SETTING_ERROR';
export const DISCARD_COMMENTING_ADDONS_SETTINGS = 'DISCARD_COMMENTING_ADDONS_SETTINGS';

export const UPLOADING_IMAGE = 'UPLOADING_IMAGE';
export const COMMUNITY_UPLOAD_IMAGE = 'COMMUNITY_UPLOAD_IMAGE';
export const COMMUNITY_DISCARD_IMAGE = 'COMMUNITY_DISCARD_IMAGE';
export const STAND_ALONE_COMMUNITY_UPLOAD_IMAGE = 'STAND_ALONE_COMMUNITY_UPLOAD_IMAGE';
export const STAND_ALONE_COMMUNITY_DISCARD_IMAGE = 'STAND_ALONE_COMMUNITY_DISCARD_IMAGE';
export const SSO_LOGO_UPLOAD_IMAGE = 'SSO_LOGO_UPLOAD_IMAGE';
export const SSO_LOGO_DISCARD_IMAGE = 'SSO_LOGO_DISCARD_IMAGE';

export const getAddOns = siteUUID => dispatch => {
    dispatch(getAddOnsFetching(true));
    return asyncGetAddOns(siteUUID)
        .then(response => {
            dispatch(getAddOnsFetching(false));
            return dispatch(getAddOnsSuccess(response.data));
        }, err => dispatch(getAddOnsFail()))
};

const getAddOnsFetching = isFetching => ({
    type: GET_COMMENTING_ADDONS_FETCHING, 
    isFetching
});

const uploadingImage = (status) => ({
    type: UPLOADING_IMAGE,
    status
});

const uploadImage = (data, imageType) => ({
    type: imageType === 'COMMUNITY' ? 
        COMMUNITY_UPLOAD_IMAGE :
        imageType === 'STAND_ALONE_COMMUNITY' ?
            STAND_ALONE_COMMUNITY_UPLOAD_IMAGE :
            SSO_LOGO_UPLOAD_IMAGE,
    data
});

export const discardImage = (imageType) => ({
    type: imageType === 'COMMUNITY' ? 
        COMMUNITY_DISCARD_IMAGE :
        imageType === 'STAND_ALONE_COMMUNITY' ?
            STAND_ALONE_COMMUNITY_DISCARD_IMAGE :
            SSO_LOGO_DISCARD_IMAGE,
    data: null
});

const getAddOnsFail = () => batchActions([
    getAddOnsFetching(false),
    getAddOnsSuccess(false),
    getAddOnsError(),
    displayErrorMessage('There was an issue fetching AddOns settings.'),
]);

const getAddOnsError = () => ({
    type: GET_COMMENTING_ADDONS_ERROR
});

const getAddOnsSuccess = (data) => ({
    type: GET_COMMENTING_ADDONS_SUCCESS,
    data
});

export const discardAddOnsSettings = ({addOnsType, ignoreProperty}) => ({
    type: DISCARD_COMMENTING_ADDONS_SETTINGS,
    addOnsType,
    ignoreProperty
})

export const addOnsSettingsChanged = ({addOnsType = null, property, value}) => ({
    type: COMMENTING_ADDONS_SETTINGS_CHANGED,
    addOnsType,
    property,
    value
})

export const updateAddOnsSetting = ({path, type, request, configurationUUID, showSuccessMessage, label = '', callback}) => dispatch => {
    return asyncUpdateAddOnsSetting({path, type, request, configurationUUID})
        .then(response => {
            if(callback) callback();
            if(showSuccessMessage)
                dispatch(displaySuccessMessage(`${label} add-ons setting updated.`));
            return dispatch(updateAddOnsSettingSuccess(type, response))
        }, err => {
            dispatch(displayErrorMessage(`There was an issue updating the ${label} add-ons setting.`));
            return dispatch(updateAddOnsSettingError(type, err));
        }
    );
};

const updateAddOnsSettingSuccess = (type, response) => ({
    type: UPDATE_COMMENTING_ADDONS_SETTING_SUCCESS,
    addOnsType: type,
    response
});

const updateAddOnsSettingError = (type, response) => ({
    type: UPDATE_COMMENTING_ADDONS_SETTING_ERROR,
    addOnsType: type,
    response
});

const uploadImageSuccess = (data, type) => batchActions([
    uploadingImage(false),
    uploadImage(data, type)
]);

const uploadImageFail = () => batchActions([
    uploadingImage(false),
    displayErrorMessage('There was an issue uploading the image.')
]);

export const uploadCommentingImage = (commentingUnitUUID, image, type, callback) => dispatch => {
    const data = new FormData();
    data.append('commentingUnitUUID', commentingUnitUUID);
    data.append('image', image, image.name);
    data.append('workflow', type);
    dispatch(uploadingImage(true));
    asyncUploadImage(data)
        .then(response => {
            dispatch(uploadImageSuccess(response.data, type))
            if (callback) callback(response.data.imageUrl)
        })
        .catch(err => {
            console.log(err);
            dispatch(uploadImageFail())
        })
}

export const createAddOnsSetting = ({path, type, request}) => dispatch => {
    return asyncCreateAddOnsSetting({path, type, request})
        .then(response => {
            return dispatch(createAddOnsSettingsSuccess(path, type, response));
        }, err => dispatch(displayErrorMessage(`There was an issue creating the add-on setting.`))
    );
};

const createAddOnsSettingsSuccess = (path, type, response) => ({
    type: CREATE_COMMENTING_ADDONS_SETTING_SUCCESS,
    addOnsType: type,
    response
});